// CUANTOS MESES DIO DINERO UNA POLIZA.
//
// Vive suelto, sin React, por el mismo motivo que comisionPorPersona.js: de
// este numero sale dinero —el total por mes es `comision mensual x meses`— y
// una cuenta metida dentro de un componente solo se puede comprobar mirandola.
//
// Lissette 2026-09-11: *"coloca una columna con la fecha de cancelacion, si
// esta cancelada, es para saber cuantos meses esa poliza dio dinero"*. Antes de
// esto, Comisiones por Poliza contaba SIEMPRE de la vigencia a la renovacion:
// una poliza anual cancelada a los cuatro meses figuraba con doce, y su comision
// salia inflada por tres.
import { esEstatusCancelado } from './policyStatuses.js';

/**
 * Meses COMPLETOS entre dos fechas.
 *
 * Se parte el `YYYY-MM-DD` y se construye la fecha con hora local (`T00:00:00`)
 * a proposito: con `new Date('2026-01-31')` el navegador entiende UTC y en
 * Florida eso es el dia 30, que en esta cuenta se convierte en un mes de menos.
 */
export function mesesEntreFechas(inicio, fin) {
  if (!inicio || !fin) return 0;
  const s = new Date(String(inicio).slice(0, 10) + 'T00:00:00');
  const e = new Date(String(fin).slice(0, 10) + 'T00:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
  let m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) m -= 1; // el ultimo mes aun no se ha cumplido
  return m;
}

/**
 * El dia despues de una fecha `YYYY-MM-DD`, en el mismo formato.
 *
 * LA FECHA FINAL CUENTA (Lissette 2026-10-11: *"si coloco vigencia del 1 de
 * enero y renovacion 31 de diciembre, ¿por que me pones 11 meses y no 12?"*).
 * Una poliza del 1/1 al 31/12 cubre el 31 de diciembre entero: son doce meses.
 * `mesesEntreFechas` cuenta meses CUMPLIDOS de una fecha a otra, y del 1/1 al
 * 31/12 todavia no se cumple el duodecimo. Contando hasta el dia siguiente al
 * ultimo dia cubierto sale bien en los dos estilos que usan las agencias:
 * 1/1 → 31/12 da 12, y 1/1 → 1/1 del año siguiente sigue dando 12.
 */
export function diaSiguiente(fecha) {
  if (!fecha) return fecha;
  const d = new Date(String(fecha).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return fecha;
  d.setDate(d.getDate() + 1);
  const dos = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/**
 * Una poliza CANCELADA dejo de dar dinero el dia que se cancelo, no el dia que
 * le tocaba renovar.
 *
 * Se piden las DOS cosas —estatus cancelado Y fecha— y no solo la fecha: hasta
 * el 2026-09-11 el formulario dejaba guardar una fecha de cancelacion en una
 * poliza que seguia activa, asi que hay filas viejas con fecha puesta y sin
 * cancelar. Contar solo hasta esa fecha en ellas rebajaria una comision que se
 * cobro entera.
 */
export function estaCancelada(poliza) {
  return !!poliza && esEstatusCancelado(poliza.status) && !!poliza.cancellationDate;
}

/**
 * { meses, asumido, porCancelacion } de una poliza.
 *
 *   porCancelacion — se conto hasta la fecha de cancelacion.
 *   asumido        — no habia fecha de renovacion y se dan por hechos 12 (anual);
 *                    la pantalla lo marca con un `*` para que se pueda corregir.
 */
export function mesesQueDioDinero(poliza) {
  if (estaCancelada(poliza)) {
    // El dia de la cancelacion tambien estuvo cubierto (ver diaSiguiente).
    const hasta = mesesEntreFechas(poliza.effectiveDate, diaSiguiente(poliza.cancellationDate));
    // Cancelada el mismo mes en que entro en vigor: dio dinero ESE mes, no cero.
    // Un cero aqui pondria a cero la comision de una poliza que si se cobro.
    return { meses: Math.max(hasta, 1), asumido: false, porCancelacion: true };
  }
  const crudo = mesesEntreFechas(poliza?.effectiveDate, diaSiguiente(poliza?.renewalDate));
  if (crudo > 0) return { meses: crudo, asumido: false, porCancelacion: false };
  return { meses: 12, asumido: true, porCancelacion: false };
}
